import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Affix, Tabs, Badge, Switch, Typography, Popover, Empty } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import {
  selectors,
  instrumentTypes,
  FinancialInstrument,
  CASH_APPROACH,
  CASH,
  fetchBlotter,
  findAFSBondMarketReplacementInstrument,
  OPTION,
  REPO,
  FUTURES,
  // fetchFwdContracts,
} from 'state';
import { formatters } from 'services';
import OrderModal from '../OrderModal';
import HedgeOrderModal from '../HedgeOrderModal';
import ODAModal from '../ODAModal';
import InstrumentTableDivisor from './InstrumentTableDivisor';
import PortfolioOverview from './PortfolioOverview';
import CashTab from './CashTab';
import ContractsModal from '../ContractsModal';
import { TraineeSessionContext } from '../TraineeSessionContext';
import RepoModal from '../RepoModal';
import CDSModal from '../CDSModal';
import CDSContractsModal from '../CDSContractsModal';
import RepoContractsModal from '../RepoContractsModal';
import TBillModal from '../TBillModal';
import TBillContractsModal from '../TBillContractsModal';
import { fetchClientMoney } from '../../../state/app/client_money/actions';
import { sessionHasOptions } from '../../../state/app/selectors';
import { fetchOptionsChains } from '../../../state/app/options/actions';
import SimplifiedOptionOrderModal from '../SimplifiedOptions/SimplifiedOptionOrderModal';
import {
  isSimplifiedOptionAsset,
  shouldShowSimplifiedOptionInMarkets,
} from '../../../state/app/simplified_options/presentation';

const MarketsTab: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const clients_fetched = useSelector(selectors.clientsFetched);
  const { risk_approach } = useSelector(selectors.activeSession);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const { fetched_blotter } = useSelector(selectors.player);
  // actually traineeContext
  const trainerContext = useContext(TraineeSessionContext);
  const player = useSelector(selectors.player);
  const hasOptions = useSelector(selectors.sessionHasOptions);

  useEffect(() => {
    if (fetched_blotter) return;
    dispatch(fetchBlotter({}));
  }, [dispatch]);

  useEffect(() => {
    if (session.client_money) {
      if (clients_fetched) return;
      dispatch(fetchClientMoney({ session_id: session.id }));
    }
    if (hasOptions) {
      dispatch(fetchOptionsChains({ session_id: session.id }));
      // dispatch(fetchOptionsContracts({ player_id: player.id }));  // currently player contracts are nested in his options instruments
      // dispatch(fetchOptionsInstruments({ player_id: player.id }));
    }
  }, [dispatch]);

  const instrumentFilter = (
    instrument: FinancialInstrument,
    type: string,
    name: string
  ) => {
    const asset = underlyingAssets[instrument.underlying_asset];
    const sameType = asset?.resourcetype === type;

    const hedge = instrument.fx_hedge === name.includes('Forwards');

    const available =
      !isSimplifiedOptionAsset(asset) ||
      shouldShowSimplifiedOptionInMarkets(instrument);

    return sameType && hedge && available;
  };

  const allFinancialInstruments = useSelector(
    selectors.playerMarketFinancialInstrumentsList,
  );
  // Filter only this Markets presentation, never the canonical book/selectors.
  const _financialInstruments = useMemo(() => {
    if (session.which_markets !== 0 || !session.markets_v5_hidden_assets?.length) return allFinancialInstruments;
    const hidden = new Set(session.markets_v5_hidden_assets);
    return allFinancialInstruments.filter(row => !hidden.has(row.underlying_asset));
  }, [allFinancialInstruments, session.which_markets, session.markets_v5_hidden_assets]);

  useEffect(() => {
    if (!trainerContext.instrumentType) {
      for (let i = 0; i < instrumentTypes.length; i++) {
        const details = instrumentTypes[i];
        const instruments = _financialInstruments.filter(fn =>
          instrumentFilter(fn, details.type, details.name)
        );

        if (instruments.length) {
          trainerContext.setInstrumentType(`${i}`);
          break;
        }
      }
    }
  }, [_financialInstruments, trainerContext]);

  // if (!trainerContext.instrumentType) {
  //   for (let i = 0; i < instrumentTypes.length; i++) {
  //     const details = instrumentTypes[i];
  //     const instruments = _financialInstruments.filter(fn =>
  //       instrumentFilter(fn, details.type, details.name)
  //     );
  //
  //     if (instruments.length) {
  //       trainerContext.setInstrumentType(`${i}`);
  //       break;
  //     }
  //   }
  // }

  const details = instrumentTypes[parseInt(trainerContext.instrumentType)] || {
    type: '',
    name: '',
  };

  const financialInstruments = _financialInstruments.filter(fn =>
    instrumentFilter(fn, details.type, details.name)
  );

  const [instrument, setInstrument] = useState<FinancialInstrument | undefined>(_financialInstruments[0]);
  const instrumentVisible = instrument && _financialInstruments.some(row => row.id === instrument.id);

  const [visibleOrderModal, setVisibleOrderModal] = useState(false);
  const [visibleHedgeOrderModal, setVisibleHedgeOrderModal] = useState(false);
  const [visibleCDSModal, setVisibleCDSModal] = useState(false);
  const [visibleTBillModal, setVisibleTBillModal] = useState(false);
  const [visibleRepoModal, setVisibleRepoModal] = useState(false);
  const [visibleContractsModal, setVisibleContractsModal] = useState(false);
  const [visibleTBillContractsModal, setVisibleTBillContractsModal] =
    useState(false);
  const [visibleRepoContractsModal, setVisibleRepoContractsModal] =
    useState(false);
  const [visibleCDSContractsModal, setVisibleCDSContractsModal] =
    useState(false);
  const [visibleODAModal, setVisibleODAModal] = useState(false);

  useEffect(() => {
    if (
      instrument && _financialInstruments.some(item => item.id === instrument.id)
    ) {
      return;
    }

    setVisibleOrderModal(false);
    setVisibleODAModal(false);
    if (instrument && session.markets_v5_hidden_assets?.includes(instrument.underlying_asset)) {
      setVisibleHedgeOrderModal(false);
      setVisibleCDSModal(false);
      setVisibleTBillModal(false);
      setVisibleRepoModal(false);
      setVisibleContractsModal(false);
      setVisibleTBillContractsModal(false);
      setVisibleRepoContractsModal(false);
      setVisibleCDSContractsModal(false);
    }
    const replacement = instrument && findAFSBondMarketReplacementInstrument(
      instrument,
      _financialInstruments,
      session.afs_bond_market_replacements,
    );
    const nextInstrument = replacement || _financialInstruments[0];
    setInstrument(nextInstrument);
    if (nextInstrument && session.markets_v5_hidden_assets?.length && !financialInstruments.length) {
      const index = instrumentTypes.findIndex(item => instrumentFilter(nextInstrument, item.type, item.name));
      if (index !== -1) trainerContext.setInstrumentType(String(index));
    }
  }, [_financialInstruments, instrument, session.afs_bond_market_replacements, session.markets_v5_hidden_assets]);

  // const [showCash, setShowCash] = useState(false);

  const { tactical_asset_allocations } = useSelector(selectors.player);

  const allocations = {} as { [assetClass: number]: string };
  tactical_asset_allocations.forEach(allocation => {
    if (allocation.percent !== 0) {
      allocations[allocation.asset_class] = formatters.percent2Digit(
        allocation.percent
      );
    }
  });

  const getTAA = (assetClass: number) => {
    if (!allocations[assetClass]) {
      return '';
    }

    return (
      <Typography.Text
        type="danger"
        style={{
          fontSize: '75%',
        }}
      >
        <> ({allocations[assetClass]})</>
      </Typography.Text>
    );
  };

  const [showActiveOrders, setShowActiveOrders] = useState(false);
  if (!trainerContext.instrumentType && !session.markets_v5_hidden_assets?.length) return null;

  return (
    <div>
      <Affix offsetTop={94}>
        <div className="markets-overview">
          <PortfolioOverview instrument={instrumentVisible ? instrument : undefined} />

          <div style={{ display: 'flex', width: '100%', gap: 15 }}>
            <Tabs
              style={{ flex: 1 }}
              defaultActiveKey={trainerContext.instrumentType}
              activeKey={trainerContext.showCash ? 'cash' : trainerContext.instrumentType}
              onChange={val => {
                if (val === 'cash') return trainerContext.setShowCash(true);

                trainerContext.setShowCash(false);

                trainerContext.setInstrumentType(val);
                setInstrument(
                  _financialInstruments.filter(fn =>
                    instrumentFilter(
                      fn,
                      instrumentTypes[parseInt(val)].type,
                      instrumentTypes[parseInt(val)].name
                    )
                  )[0] || instrument
                );
              }}
            >
              {risk_approach === CASH_APPROACH && (
                <Tabs.TabPane
                  key="cash"
                  tab={<span>Cash{getTAA(CASH)}</span>}
                />
              )}

              {instrumentTypes.map((item, i) => {
                if (item.assetClass === OPTION) {
                  // options are displayed in their own 'OptionsTab' component at /options
                  return;
                }
                if (item.assetClass === REPO && session.client_money) {
                  // in client dealing sessions, repos are displayed in the Central Bank tab
                  return;
                }
                // console.log('item: ', item);
                const instruments = _financialInstruments.filter(fn =>
                  instrumentFilter(fn, item.type, item.name)
                );

                if (!instruments.length) return null;

                const activeInstruments = instruments.filter(fn => fn.amount);

                if (showActiveOrders && !activeInstruments.length) {
                  return null;
                }

                return (
                  <Tabs.TabPane
                    key={`${i}`}
                    tab={
                      <span className="markets-overview__tab-label">
                        <span>
                          {item.name}
                          {getTAA(item.assetClass)}
                          {item.assetClass === FUTURES &&
                            session.futures_rollover_periods > 0 && (
                              <Popover
                                content={`Future contracts automatically rollover. Rollover fee is ${+(
                                  session.futures_rollover_fee_pct * 100
                                ).toFixed(2)} % of position value.`}
                              >
                                <InfoCircleOutlined
                                  style={{ marginLeft: 6, color: '#8c8c8c' }}
                                />
                              </Popover>
                            )}
                        </span>
                        {activeInstruments.length > 0 && (
                          <Badge count={activeInstruments.length} />
                        )}
                      </span>
                    }
                  />
                );
              })}
            </Tabs>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                paddingBottom: 15,
              }}
            >
              Show active trades
              <Switch
                checked={showActiveOrders}
                onChange={setShowActiveOrders}
              />
            </div>
          </div>
        </div>
      </Affix>

      {trainerContext.showCash ? (
        <CashTab />
      ) : !_financialInstruments.length ? (
        <Empty description="No instruments are visible in Markets" />
      ) : (
        <InstrumentTableDivisor
          financialInstruments={financialInstruments}
          selectedInstrumentId={instrument?.id || 0}
          selectInstrument={setInstrument}
          showOrderModal={() => setVisibleOrderModal(true)}
          showHedgeOrderModal={() => setVisibleHedgeOrderModal(true)}
          showCDSModal={() => setVisibleCDSModal(true)}
          showTBillModal={() => setVisibleTBillModal(true)}
          showRepoModal={() => setVisibleRepoModal(true)}
          showContractsModal={() => setVisibleContractsModal(true)}
          showCDSContractsModal={() => setVisibleCDSContractsModal(true)}
          showTBillContractsModal={() => {
            // console.log('showTBillContractsModal');
            setVisibleTBillContractsModal(true);
          }}
          showRepoContractsModal={() => setVisibleRepoContractsModal(true)}
          showODAModal={() => setVisibleODAModal(true)}
          showActiveOrders={showActiveOrders}
        />
      )}

      {visibleOrderModal &&
        instrumentVisible && instrument &&
        (isSimplifiedOptionAsset(
          underlyingAssets[instrument.underlying_asset]
        ) ? (
          <SimplifiedOptionOrderModal
            visible={visibleOrderModal}
            hide={() => setVisibleOrderModal(false)}
            id={instrument.id}
          />
        ) : (
          <OrderModal
            visible={visibleOrderModal}
            hide={() => setVisibleOrderModal(false)}
            id={instrument.id}
          />
        ))}
      {visibleHedgeOrderModal && instrumentVisible && instrument && (
        <HedgeOrderModal
          visible={visibleHedgeOrderModal}
          hide={() => setVisibleHedgeOrderModal(false)}
          id={instrument.id}
        />
      )}
      {visibleCDSModal && instrumentVisible && instrument && (
        <CDSModal
          visible={visibleCDSModal}
          hide={() => setVisibleCDSModal(false)}
          id={instrument.id}
        />
      )}
      {visibleTBillModal && instrumentVisible && instrument && (
        <TBillModal
          visible={visibleTBillModal}
          hide={() => setVisibleTBillModal(false)}
          id={instrument.id}
        />
      )}
      {visibleRepoModal && instrumentVisible && instrument && (
        <RepoModal
          visible={visibleRepoModal}
          hide={() => setVisibleRepoModal(false)}
          id={instrument.id}
        />
      )}
      {visibleContractsModal && instrumentVisible && instrument && (
        <ContractsModal
          visible={visibleContractsModal}
          hide={() => setVisibleContractsModal(false)}
          id={instrument.id}
        />
      )}
      {visibleRepoContractsModal && instrumentVisible && instrument && (
        <RepoContractsModal
          visible={visibleRepoContractsModal}
          hide={() => setVisibleRepoContractsModal(false)}
          id={instrument.id}
        />
      )}
      {visibleCDSContractsModal && instrumentVisible && instrument && (
        <CDSContractsModal
          visible={visibleCDSContractsModal}
          hide={() => setVisibleCDSContractsModal(false)}
          id={instrument.id}
        />
      )}
      {visibleTBillContractsModal && instrumentVisible && instrument && (
        <TBillContractsModal
          visible={visibleTBillContractsModal}
          hide={() => setVisibleTBillContractsModal(false)}
          id={instrument.id}
        />
      )}
      {visibleODAModal && instrumentVisible && instrument && (
        <ODAModal
          visible={visibleODAModal}
          hide={() => setVisibleODAModal(false)}
          id={instrument.id}
        />
      )}
    </div>
  );
};

export default MarketsTab;
